import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  ConversationDetailDto,
  ConversationListItemDto,
  CreateGroupChatRequest,
  InboxUnreadResponse,
  MessageDto,
  PostMessageRequest,
} from '@cosvision/shared';
import * as api from '../lib/api/conversations.js';
import { qk } from '../lib/query-keys.js';

const LIST_POLL_MS = 5_000;
const THREAD_POLL_MS = 5_000;
const BADGE_POLL_MS = 20_000;
/** Initial thread load size — matches the server's max page (listMessagesQuerySchema). */
const MESSAGE_PAGE_SIZE = 100;

export function useConversationsQuery(enabled = true): UseQueryResult<ConversationListItemDto[]> {
  return useQuery({
    queryKey: qk.conversations.list(),
    queryFn: () => api.listConversations(),
    enabled,
    refetchInterval: LIST_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 2_000,
  });
}

export function useInboxUnreadQuery(enabled: boolean): UseQueryResult<InboxUnreadResponse> {
  return useQuery({
    queryKey: qk.conversations.unread(),
    queryFn: () => api.getInboxUnread(),
    enabled,
    refetchInterval: BADGE_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  });
}

export function useConversationQuery(
  id: string | undefined,
): UseQueryResult<ConversationDetailDto> {
  return useQuery({
    queryKey: qk.conversations.detail(id ?? 'none'),
    queryFn: () => api.getConversation(id as string),
    enabled: Boolean(id),
    staleTime: 10_000,
    // A 404 on a non-existent / non-participant conversation should not trigger
    // three retries — fail fast so the UI can render an error state.
    retry: false,
  });
}

export function useMessagesQuery(id: string | undefined): UseQueryResult<MessageDto[]> {
  return useQuery({
    queryKey: qk.conversations.messages(id ?? 'none'),
    queryFn: () => api.listMessages(id as string, { limit: MESSAGE_PAGE_SIZE }),
    enabled: Boolean(id),
    // Stop polling once a fetch errored (typically a 404 — the conversation
    // was deleted, the user left, or someone typed a bad URL). Without this
    // we hammer the backend every 5s with the same failing request.
    refetchInterval: (q) => (id && !q.state.error ? THREAD_POLL_MS : false),
    refetchIntervalInBackground: false,
    staleTime: 1_000,
    retry: false,
  });
}

function useMessagingInvalidator() {
  const qc = useQueryClient();
  return (conversationId?: string) => {
    void qc.invalidateQueries({ queryKey: qk.conversations.list() });
    void qc.invalidateQueries({ queryKey: qk.conversations.unread() });
    if (conversationId) {
      void qc.invalidateQueries({ queryKey: qk.conversations.messages(conversationId) });
    }
  };
}

export function useSendMessageMutation(conversationId: string) {
  const invalidate = useMessagingInvalidator();
  return useMutation({
    mutationFn: (body: PostMessageRequest) => api.sendMessage(conversationId, body),
    onSuccess: () => invalidate(conversationId),
  });
}

export function useEditMessageMutation(conversationId: string) {
  const invalidate = useMessagingInvalidator();
  return useMutation({
    mutationFn: (vars: { msgId: string; body: string }) =>
      api.editMessage(conversationId, vars.msgId, { body: vars.body }),
    onSuccess: () => invalidate(conversationId),
  });
}

export function useDeleteMessageMutation(conversationId: string) {
  const invalidate = useMessagingInvalidator();
  return useMutation({
    mutationFn: (msgId: string) => api.deleteMessage(conversationId, msgId),
    onSuccess: () => invalidate(conversationId),
  });
}

export function useMarkReadMutation() {
  const invalidate = useMessagingInvalidator();
  return useMutation({
    mutationFn: (conversationId: string) => api.markConversationRead(conversationId),
    onSuccess: (_d, conversationId) => invalidate(conversationId),
  });
}

export function useCreateDmMutation() {
  const invalidate = useMessagingInvalidator();
  return useMutation({
    mutationFn: (userId: string) => api.createDm(userId),
    onSuccess: () => invalidate(),
  });
}

export function useCreateGroupMutation() {
  const invalidate = useMessagingInvalidator();
  return useMutation({
    mutationFn: (body: CreateGroupChatRequest) => api.createGroupChat(body),
    onSuccess: () => invalidate(),
  });
}
